import React, { useEffect, useState } from "react"
import { MdBookmarkAdd, MdClose, MdOutlineBookmark, MdOutlineDelete, MdOutlineStar, MdStar } from "react-icons/md"
import { toast } from "react-toastify"
import ModalConfirm from "../Modal/ModalConfirm"
import { createSavedLeadFilter, deleteSavedLeadFilter, getSavedLeadFilters, updateSavedLeadFilter } from "../../schemas/SavedLeadFilter"
import styles from "./leadFilters.module.css"
import { resolveRelativeLeadFilterDates } from "../../utils/leadFilters"
import Button from "../Form/Button"
import Input from "../Form/Input"

const SavedFilters = ({ scope, filters = {}, onLoad = () => { } }) => {
    const [savedFilters, setSavedFilters] = useState([])
    const [name, setName] = useState("")
    const [loading, setLoading] = useState(false)
    const [presetToDelete, setPresetToDelete] = useState(null)

    const loadSavedFilters = async () => {
        try {
            setLoading(true)
            setSavedFilters(await getSavedLeadFilters(scope))
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar os filtros salvos.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (scope) loadSavedFilters()
    }, [scope])

    const saveCurrentFilter = async () => {
        const normalizedName = name.trim()
        if (!normalizedName) {
            toast.info("Informe um nome para o filtro.")
            return
        }

        try {
            setLoading(true)
            const existing = savedFilters.find(item => item.name.toLocaleLowerCase("pt-BR") === normalizedName.toLocaleLowerCase("pt-BR"))
            if (existing) {
                await updateSavedLeadFilter(existing._id, { filters })
                toast.success("Filtro salvo atualizado.")
            } else {
                await createSavedLeadFilter({ name: normalizedName, scope, filters })
                toast.success("Filtro salvo com sucesso.")
            }
            setName("")
            await loadSavedFilters()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível salvar o filtro.")
        } finally {
            setLoading(false)
        }
    }

    const removeSavedFilter = async preset => {
        setPresetToDelete(null)
        try {
            setLoading(true)
            await deleteSavedLeadFilter(preset._id)
            setSavedFilters(previous => previous.filter(item => item._id !== preset._id))
            toast.success("Filtro salvo excluído.")
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível excluir o filtro.")
        } finally {
            setLoading(false)
        }
    }

    const toggleDefault = async preset => {
        try {
            setLoading(true)
            await updateSavedLeadFilter(preset._id, { is_default: !preset.is_default })
            await loadSavedFilters()
            toast.success(preset.is_default ? "Filtro padrão removido." : "Filtro definido como padrão.")
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível alterar o filtro padrão.")
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <section className={styles.savedFilters} aria-label="Filtros salvos">
            <div className={styles.savedFiltersHeader}>
                <div>
                    <strong>Filtros salvos</strong>
                    <span>Sincronizados em todos os seus dispositivos</span>
                </div>
                <MdOutlineBookmark />
            </div>

            {savedFilters.length > 0 &&
                <div className={styles.savedFilterList}>
                    {savedFilters.map(preset => (
                        <div className={`${styles.savedFilterItem} ${preset.is_default ? styles.savedFilterDefault : ""}`} key={preset._id}>
                            <button type="button" className={styles.savedFilterApply} onClick={() => onLoad(resolveRelativeLeadFilterDates(JSON.parse(JSON.stringify(preset.filters))))} title={`Carregar ${preset.name}`} disabled={loading}>
                                {preset.name}
                            </button>
                            <button type="button" className={styles.savedFilterDefaultAction} onClick={() => toggleDefault(preset)} aria-label={preset.is_default ? `Remover ${preset.name} como padrão` : `Definir ${preset.name} como padrão`} title={preset.is_default ? "Filtro padrão" : "Definir como padrão"} disabled={loading}>
                                {preset.is_default ? <MdStar /> : <MdOutlineStar />}
                            </button>
                            <button type="button" className={styles.savedFilterRemove} onClick={() => setPresetToDelete(preset)} aria-label={`Excluir ${preset.name}`} disabled={loading}>
                                <MdClose />
                            </button>
                        </div>
                    ))}
                </div>
            }

            <div className={styles.saveFilterForm}>
                <Input
                    type="text"
                    value={name}
                    onChange={event => setName(event.target.value)}
                    onKeyDown={event => event.key === "Enter" && saveCurrentFilter()}
                    placeholder="Nome desta combinação"
                    maxLength={50}
                    aria-label="Nome do filtro salvo"
                    disabled={loading}
                />
                <Button type="button" fullWidth={false} onClick={saveCurrentFilter} title="Salvar combinação atual" disabled={loading}>
                    <MdBookmarkAdd />
                    Salvar
                </Button>
            </div>
            </section>
            {presetToDelete &&
                <ModalConfirm
                    icon={<MdOutlineDelete />}
                    title="Excluir filtro salvo?"
                    description={presetToDelete.name}
                    text="Esta combinação de filtros será removida de todos os seus dispositivos."
                    confirmLabel="Excluir"
                    confirmVariant="danger"
                    closeModal={() => setPresetToDelete(null)}
                    onSubmit={() => removeSavedFilter(presetToDelete)}
                />
            }
        </>
    )
}

export default SavedFilters
